import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, Clock, Shield } from 'lucide-react';
import { contactInfo } from '../mockData';
import TerritoryCalculator from '../components/TerritoryCalculator';

const Contact = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    organizationType: '',
    fullName: '',
    email: '',
    phone: '',
    organization: '',
    country: '',
    subject: '',
    message: '',
    clearanceLevel: '',
    timeline: ''
  });

  const [calculatorData, setCalculatorData] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCalculatorDataChange = (data) => {
    setCalculatorData(data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contact_info: formData,
          territory_data: calculatorData || {}
        })
      });

      if (response.ok) {
        console.log('Quote request submitted successfully');
        setSubmitted(true);
        
        setTimeout(() => {
          setSubmitted(false);
          setFormData({
            organizationType: '',
            fullName: '',
            email: '',
            phone: '',
            organization: '',
            country: '',
            subject: '',
            message: '',
            clearanceLevel: '',
            timeline: ''
          });
          setCalculatorData(null);
        }, 3000);
      } else {
        console.error('Failed to submit quote');
        alert('Failed to submit quote. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      alert('Error submitting quote. Please check your connection.');
    }
  };

  return (
    <div className="dark-page contact-page">
      {/* Contact Hero */}
      <section className="contact-hero-pro">
        <div className="section-tag" style={{ textAlign: 'center', display: 'block', width: '100%', marginBottom: '28px' }}>DEPLOYMENT PLANNING</div>
        <h1 className="defense-headline" style={{ fontSize: '72px', textAlign: 'center', marginBottom: '28px' }}>Request Quote</h1>
        <p className="defense-description" style={{ maxWidth: '900px', margin: '0 auto 40px', textAlign: 'center', fontSize: '19px', lineHeight: '1.7' }}>
          Calculate your territory protection needs and request a detailed deployment brief from our team.
        </p>
      </section>

      {/* Territory Calculator */}
      <section className="calculator-section">
        <TerritoryCalculator onDataChange={handleCalculatorDataChange} />
      </section>

      {/* Contact Form Section */}
      <section style={{ 
        padding: '80px 20px', 
        background: 'linear-gradient(180deg, #000000 0%, rgba(0, 0, 0, 0.95) 100%)',
        borderTop: '1px solid rgba(212, 175, 55, 0.1)'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {/* Section Header */}
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <div style={{ 
              fontSize: '12px', 
              fontWeight: '700', 
              color: '#D4AF37', 
              letterSpacing: '2px', 
              marginBottom: '16px' 
            }}>
              REQUEST QUOTE
            </div>
            <h2 style={{ 
              fontSize: '48px', 
              fontWeight: '700', 
              color: '#FFFFFF', 
              marginBottom: '16px',
              lineHeight: '1.2'
            }}>
              Request Technical Brief
            </h2>
            <p style={{ 
              fontSize: '18px', 
              color: 'rgba(255, 255, 255, 0.7)', 
              maxWidth: '600px', 
              margin: '0 auto' 
            }}>
              Submit your requirements for a detailed deployment assessment
            </p>
          </div>

          {/* Premium Form */}
          {submitted ? (
            <div style={{
              textAlign: 'center',
              padding: '80px 40px',
              background: 'rgba(212, 175, 55, 0.05)',
              border: '1px solid rgba(212, 175, 55, 0.3)'
            }}>
              <CheckCircle size={64} style={{ color: '#D4AF37', margin: '0 auto 24px' }} />
              <h3 style={{ fontSize: '28px', fontWeight: '700', color: '#FFFFFF', marginBottom: '12px' }}>
                Request Submitted
              </h3>
              <p style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.7)' }}>
                Our team will contact you within 24 hours
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ 
              background: 'rgba(0, 0, 0, 0.5)', 
              border: '1px solid rgba(212, 175, 55, 0.2)',
              padding: '48px'
            }}>
              {/* Organization Type */}
              <div style={{ marginBottom: '32px' }}>
                <label style={{ 
                  fontSize: '12px', 
                  fontWeight: '700', 
                  color: '#D4AF37', 
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  marginBottom: '12px',
                  display: 'block'
                }}>
                  Organization Type *
                </label>
                <select 
                  name="organizationType" 
                  value={formData.organizationType} 
                  onChange={handleChange} 
                  required
                  style={{ 
                    width: '100%',
                    padding: '16px 20px',
                    background: '#000000',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    color: '#FFFFFF',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#D4AF37'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(212, 175, 55, 0.3)'}
                >
                  <option value="" style={{ background: '#000000', color: '#666666' }}>Select organization type</option>
                  <option value="military" style={{ background: '#000000', color: '#FFFFFF' }}>Military / Defense</option>
                  <option value="government" style={{ background: '#000000', color: '#FFFFFF' }}>Government Agency</option>
                  <option value="security" style={{ background: '#000000', color: '#FFFFFF' }}>Private Security</option>
                  <option value="infrastructure" style={{ background: '#000000', color: '#FFFFFF' }}>Critical Infrastructure</option>
                </select>
              </div>

              {/* Name and Email Row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
                <div>
                  <label style={{ 
                    fontSize: '12px', 
                    fontWeight: '700', 
                    color: '#D4AF37', 
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    marginBottom: '12px',
                    display: 'block'
                  }}>
                    Full Name *
                  </label>
                  <input 
                    type="text" 
                    name="fullName" 
                    value={formData.fullName} 
                    onChange={handleChange} 
                    required 
                    placeholder="e.g., Col. John Anderson"
                    style={{ 
                      width: '100%',
                      padding: '16px 20px',
                      background: '#000000',
                      border: '1px solid rgba(212, 175, 55, 0.3)',
                      color: '#FFFFFF',
                      fontSize: '16px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#D4AF37'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(212, 175, 55, 0.3)'}
                  />
                </div>
                <div>
                  <label style={{ 
                    fontSize: '12px', 
                    fontWeight: '700', 
                    color: '#D4AF37', 
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    marginBottom: '12px',
                    display: 'block'
                  }}>
                    Email *
                  </label>
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email} 
                    onChange={handleChange} 
                    required 
                    placeholder="your.email@defense.mil"
                    style={{ 
                      width: '100%',
                      padding: '16px 20px',
                      background: '#000000',
                      border: '1px solid rgba(212, 175, 55, 0.3)',
                      color: '#FFFFFF',
                      fontSize: '16px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#D4AF37'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(212, 175, 55, 0.3)'}
                  />
                </div>
              </div>

              {/* Phone and Organization Row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
                <div>
                  <label style={{ 
                    fontSize: '12px', 
                    fontWeight: '700', 
                    color: '#D4AF37', 
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    marginBottom: '12px',
                    display: 'block'
                  }}>
                    Phone *
                  </label>
                  <input 
                    type="tel" 
                    name="phone" 
                    value={formData.phone} 
                    onChange={handleChange} 
                    required 
                    placeholder="+1 (555) 123-4567"
                    style={{ 
                      width: '100%',
                      padding: '16px 20px',
                      background: '#000000',
                      border: '1px solid rgba(212, 175, 55, 0.3)',
                      color: '#FFFFFF',
                      fontSize: '16px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#D4AF37'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(212, 175, 55, 0.3)'}
                  />
                </div>
                <div>
                  <label style={{ 
                    fontSize: '12px', 
                    fontWeight: '700', 
                    color: '#D4AF37', 
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    marginBottom: '12px',
                    display: 'block'
                  }}>
                    Organization *
                  </label>
                  <input 
                    type="text" 
                    name="organization" 
                    value={formData.organization} 
                    onChange={handleChange} 
                    required 
                    placeholder="e.g., NATO Defense Command"
                    style={{ 
                      width: '100%',
                      padding: '16px 20px',
                      background: '#000000',
                      border: '1px solid rgba(212, 175, 55, 0.3)',
                      color: '#FFFFFF',
                      fontSize: '16px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#D4AF37'}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(212, 175, 55, 0.3)'}
                  />
                </div>
              </div>

              {/* Message */}
              <div style={{ marginBottom: '40px' }}>
                <label style={{ 
                  fontSize: '12px', 
                  fontWeight: '700', 
                  color: '#D4AF37', 
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  marginBottom: '12px',
                  display: 'block'
                }}>
                  Message *
                </label>
                <textarea 
                  name="message" 
                  value={formData.message} 
                  onChange={handleChange} 
                  rows="6" 
                  required 
                  placeholder="Describe your deployment requirements, territory specifications, and timeline..."
                  style={{ 
                    width: '100%',
                    padding: '16px 20px',
                    background: '#000000',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    color: '#FFFFFF',
                    fontSize: '16px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#D4AF37'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(212, 175, 55, 0.3)'}
                />
              </div>

              {/* Submit Button */}
              <button 
                type="submit" 
                style={{ 
                  width: '100%',
                  padding: '20px 40px',
                  background: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%)',
                  border: 'none',
                  color: '#000000',
                  fontSize: '16px',
                  fontWeight: '700',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(212, 175, 55, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <span>Submit Request</span>
                <Send size={20} />
              </button>
            </form>
          )}

          {/* Contact Information Cards - REDESIGNED */}
          <div style={{ marginTop: '80px' }}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <div style={{ 
                fontSize: '12px', 
                fontWeight: '700', 
                color: 'rgba(212, 175, 55, 0.7)', 
                letterSpacing: '2px', 
                marginBottom: '12px' 
              }}>
                ALTERNATIVE CONTACT
              </div>
              <h3 style={{ 
                fontSize: '32px', 
                fontWeight: '700', 
                color: '#FFFFFF', 
                marginBottom: '8px'
              }}>
                Get in Touch
              </h3>
            </div>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(4, 1fr)', 
              gap: '20px',
              maxWidth: '1600px',
              margin: '0 auto'
            }}>
              {/* Email Card */}
              <div style={{ 
                background: 'rgba(0, 0, 0, 0.5)', 
                border: '1px solid rgba(212, 175, 55, 0.2)',
                padding: '32px 24px',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '240px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#D4AF37';
                e.currentTarget.style.background = 'rgba(212, 175, 55, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.2)';
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
              }}>
                <div style={{ 
                  width: '64px', 
                  height: '64px', 
                  background: 'rgba(212, 175, 55, 0.1)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px'
                }}>
                  <Mail size={32} style={{ color: '#D4AF37' }} />
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  fontWeight: '700', 
                  color: '#D4AF37', 
                  letterSpacing: '1px',
                  marginBottom: '12px',
                  textTransform: 'uppercase'
                }}>
                  Email
                </div>
                <a 
                  href={`mailto:${contactInfo.email}`} 
                  style={{ 
                    fontSize: '15px', 
                    fontWeight: '600', 
                    color: '#FFFFFF',
                    textDecoration: 'none',
                    display: 'block',
                    marginBottom: '8px',
                    wordBreak: 'break-word',
                    lineHeight: '1.3'
                  }}
                >
                  {contactInfo.email}
                </a>
                <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)', margin: 0, lineHeight: '1.5' }}>
                  Technical inquiries
                </p>
              </div>

              {/* Phone Card */}
              <div style={{ 
                background: 'rgba(0, 0, 0, 0.5)', 
                border: '1px solid rgba(212, 175, 55, 0.2)',
                padding: '32px 24px',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '240px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#D4AF37';
                e.currentTarget.style.background = 'rgba(212, 175, 55, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.2)';
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
              }}>
                <div style={{ 
                  width: '64px', 
                  height: '64px', 
                  background: 'rgba(212, 175, 55, 0.1)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px'
                }}>
                  <Phone size={32} style={{ color: '#D4AF37' }} />
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  fontWeight: '700', 
                  color: '#D4AF37', 
                  letterSpacing: '1px',
                  marginBottom: '12px',
                  textTransform: 'uppercase'
                }}>
                  Phone
                </div>
                <a 
                  href={`tel:${contactInfo.phone}`} 
                  style={{ 
                    fontSize: '16px', 
                    fontWeight: '600', 
                    color: '#FFFFFF',
                    textDecoration: 'none',
                    display: 'block',
                    marginBottom: '8px'
                  }}
                >
                  {contactInfo.phone}
                </a>
                <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)', margin: 0, lineHeight: '1.5' }}>
                  Urgent defense solutions
                </p>
              </div>

              {/* Location Card */}
              <div style={{ 
                background: 'rgba(0, 0, 0, 0.5)', 
                border: '1px solid rgba(212, 175, 55, 0.2)',
                padding: '32px 24px',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '240px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#D4AF37';
                e.currentTarget.style.background = 'rgba(212, 175, 55, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.2)';
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
              }}>
                <div style={{ 
                  width: '64px', 
                  height: '64px', 
                  background: 'rgba(212, 175, 55, 0.1)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px'
                }}>
                  <MapPin size={32} style={{ color: '#D4AF37' }} />
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  fontWeight: '700', 
                  color: '#D4AF37', 
                  letterSpacing: '1px',
                  marginBottom: '12px',
                  textTransform: 'uppercase'
                }}>
                  Location
                </div>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: '600', 
                  color: '#FFFFFF',
                  marginBottom: '8px'
                }}>
                  {contactInfo.address}
                </div>
                <p style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)', margin: 0, lineHeight: '1.5' }}>
                  NATO certified facility
                </p>
              </div>

              {/* Business Hours Card */}
              <div style={{ 
                background: 'rgba(0, 0, 0, 0.5)', 
                border: '1px solid rgba(212, 175, 55, 0.2)',
                padding: '32px 24px',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '240px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#D4AF37';
                e.currentTarget.style.background = 'rgba(212, 175, 55, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.2)';
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
              }}>
                <div style={{ 
                  width: '64px', 
                  height: '64px', 
                  background: 'rgba(212, 175, 55, 0.1)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px'
                }}>
                  <Clock size={32} style={{ color: '#D4AF37' }} />
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  fontWeight: '700', 
                  color: '#D4AF37', 
                  letterSpacing: '1px',
                  marginBottom: '12px',
                  textTransform: 'uppercase'
                }}>
                  Business Hours
                </div>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: '600', 
                  color: '#FFFFFF',
                  marginBottom: '16px'
                }}>
                  {contactInfo.businessHours}
                </div>
                <div style={{ 
                  padding: '12px 16px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  width: '100%'
                }}>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#D4AF37',
                    fontWeight: '600',
                    marginBottom: '4px'
                  }}>
                    <Shield size={14} style={{ display: 'inline', marginRight: '6px' }} />
                    24/7 Emergency
                  </div>
                  <p style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
                    Critical situations
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
